import { MarketplacePartnerTierType } from '@atlassian/mpac-types/graphql-gateway-types';

import type { BundleConfig } from './bundleConfig';

import CollaborationBundleHero from '../assets/collab_flow_bundle_tile.png';
import FooterLeftImage from '../assets/collaboration/collab_footer_left_image.png';
import FooterRightImage from '../assets/collaboration/collab_footer_right_image.png';
import CollaborationBundleHeader from '../assets/collaboration/collab_header.svg';
import Opportunity1Icon from '../assets/collaboration/collab_opportunity1_icon.svg';
import Opportunity2Icon from '../assets/collaboration/collab_opportunity2_icon.svg';
import Opportunity3Icon from '../assets/collaboration/collab_opportunity3_icon.svg';
import Opportunity4Icon from '../assets/collaboration/collab_opportunity4_icon.svg';

const COLLABORATION_APPS = [
  {
    title: 'Structure by Tempo - Jira',
    description: 'Cross-project hierarchy',
    icon: 'https://marketplace.atlassian.com/product-listing/files/0e2550de-9973-43e8-9845-044075110a9b?fileType=image&mode=full-fit',
    iconAlt: 'Structure by Tempo Icon',
    url: 'https://marketplace.atlassian.com/apps/34717/structure-by-tempo-jira-portfolio-management-ppm',
  },
  {
    title: 'Activity Timeline: Resource Capacity Planning...',
    description: 'Team capacity planning',
    icon: 'https://marketplace.atlassian.com/product-listing/files/e91556fe-b354-4d1e-8284-f0e2c6e9acdb?fileType=image&mode=full-fit',
    iconAlt: 'Activity Timeline Icon',
    url: 'https://marketplace.atlassian.com/apps/1220860/activity-timeline-resource-capacity-planning-time-tracking',
  },
  {
    title: 'OKR Board for Jira',
    description: 'Goal tracking board',
    icon: 'https://marketplace.atlassian.com/product-listing/files/0043e6fb-3b9f-4f62-8c02-2c3441c508b9?fileType=image&mode=full-fit',
    iconAlt: 'OKR Board Icon',
    url: 'https://marketplace.atlassian.com/apps/1219988/okr-board-for-jira-by-oboard',
  },
];

const getCollaborationBenefits = (terminology: string) => ({
  title: 'Break down the barriers to seamless collaboration',
  description:
    "Misaligned timelines, tracking inconsistencies, and communication gaps can stand in the way of your team's productivity.",
  items: [
    {
      title: 'Lack of visibility into shared timelines',
      description:
        'When multiple teams work on interdependent tasks without a shared view of deadlines, blockers, or progress, it leads to misalignment, missed handoffs, and delivery delays.',
      icon: Opportunity1Icon,
      iconAlt: 'Visibility Icon',
    },
    {
      title: 'Inconsistent ways of tracking work',
      description: `Different teams often use different Jira projects, ${terminology}, or workflows, making it difficult to aggregate updates or manage progress across initiatives at scale.`,
      icon: Opportunity2Icon,
      iconAlt: 'Tracking Icon',
    },
    {
      title: 'Communication gaps between teams',
      description:
        'Without clear ownership, frequent updates, and centralized collaboration, teams struggle to stay in sync - especially in remote or hybrid environments.',
      icon: Opportunity3Icon,
      iconAlt: 'Communication Icon',
    },
    {
      title: 'Aligning priorities and resource allocation',
      description:
        'Teams may be competing for shared resources, working on misaligned goals, or unaware of broader program priorities, which causes inefficiencies and friction.',
      icon: Opportunity4Icon,
      iconAlt: 'Priorities Icon',
    },
  ],
});

const COLLABORATION_APPS_SECTIONS = [
  {
    title: 'Gain visibility into shared timelines and dependencies',
    description:
      'Get a clear, unified view of project timelines and dependencies, allowing your team to manage interdependencies and avoid misalignment or delays.',
    appDetails: {
      title: 'Structure by Tempo – Jira Project Management PPM',
      vendor: 'by Tempo Software',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.6/4',
      reviewCount: '388',
      installOrDownloadsText: '14.7k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'Sq66PxHON90',
  },
  {
    title: 'Unify work tracking across teams',
    description:
      'Centralize the tracking of tasks, deadlines, and resources, helping your team stay on the same page and manage progress efficiently, regardless of their individual workflows.',
    appDetails: {
      title: 'Activity Timeline: Resource Capacity Planning, Time Tracking & Reports',
      vendor: 'by Reliex',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.8/4',
      reviewCount: '105',
      installOrDownloadsText: '3.9k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'EmSVpM8Rnk8',
  },
  {
    title: 'Centralize communication and stay aligned',
    description:
      'Help your team stay aligned on priorities by providing a centralized platform for goal-setting and tracking progress towards objectives, ensuring transparent communication and clear ownership.',
    appDetails: {
      title: 'OKR Board for Jira by Oboard',
      vendor: 'by Oboard',
      partnerTier: MarketplacePartnerTierType.Gold,
      rating: '3.8/4',
      reviewCount: '103',
      installOrDownloadsText: '3.6k installs',
      badge: 'cloud-security' as const,
    },
    videoId: 's0p5z02_Ceo',
  },
];

export function collaborationBundleConfig(enableIssueTerminology: boolean): BundleConfig {
  const terminology = enableIssueTerminology ? 'work types' : 'issue types';
  return {
    hero: {
      breadcrumbTitle: 'Collaboration Bundle',
      title: 'Collaboration Flow Optimizer',
      description:
        'Align teams and accelerate delivery with unified timelines, standardized tracking, and seamless cross-team collaboration.',
      requestButton: 'Request now',
      headerImage: CollaborationBundleHeader,
      headerImageAlt: 'Collaboration Bundle Header Image',
      heroImage: CollaborationBundleHero,
      heroImageAlt: 'Collaboration Bundle Hero Image',
    },
    apps: COLLABORATION_APPS,
    benefits: getCollaborationBenefits(terminology),
    appsSection: {
      title: 'An easier way to work better together',
      description:
        'Empower your team with solutions that streamline processes, enhance visibility, and foster seamless collaboration across all projects.',
      sections: COLLABORATION_APPS_SECTIONS,
    },
    footer: {
      title: 'Ready to align your teams and timelines?',
      description:
        'Streamline collaboration, improve visibility, and keep cross-functional projects moving forward.',
      requestButton: 'Request now',
      leftImageAlt: 'Team collaboration left',
      rightImageAlt: 'Team collaboration right',
      leftImage: FooterLeftImage,
      rightImage: FooterRightImage,
    },
  };
}
